import React from 'react';
import {
  alpha,
  buttonBaseClasses,
  SxProps,
  Theme,
  ToggleButton,
  toggleButtonClasses,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';

import { DesignLogoIcon, LayoutLogoIcon } from '@paragon/material';

import { useAppMonitor } from '../../../monitor';
import { setRenderModeAndEnsureSelectedItemIsAppropriate } from '../../../store/actions/cameraActions';
import { isLayoutCameraInUse, isLayoutModeEnabled } from '../../../store/selectors/layoutModeSelectors';
import { useAppDispatch, useAppSelector, useIsClientInMobileMode } from '../../hooks';

export function SimpleModeToggle(props: { sx?: SxProps<Theme>; buttonSx?: SxProps<Theme> }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const mobile = useIsClientInMobileMode();

  const layoutModeEnabled = useAppSelector(isLayoutModeEnabled);
  const layoutCameraActive = useAppSelector(isLayoutCameraInUse);

  if (!layoutModeEnabled) return null;

  return (
    <ToggleButtonGroup
      sx={[
        {
          [`& .${buttonBaseClasses.root}.${toggleButtonClasses.root}`]: {
            paddingY: 1,
            display: 'flex',
            gap: 1,

            backgroundColor: alpha('#000', 0.08),
            '&:hover': { backgroundColor: (theme) => alpha('#000', 0.08 + theme.palette.action.hoverOpacity) },

            [`&.${toggleButtonClasses.selected}`]: {
              backgroundColor: `${alpha('#fff', 0.08)}`,
              '&:hover': { backgroundColor: (theme) => alpha('#fff', 0.08 + theme.palette.action.hoverOpacity) },
            },
          },

          [`& .${toggleButtonClasses.root}:not(.${toggleButtonClasses.selected}) svg`]: {
            filter: 'grayscale(0.85)',
          },
        },

        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
      size={mobile ? 'small' : 'medium'}
      value={layoutCameraActive ? 'layout' : 'design'}
    >
      <Tooltip disableInteractive title="Layout Mode (Ctrl + 1)">
        <span>
          <ToggleButton
            value="layout"
            sx={props.buttonSx}
            onClick={() => {
              monitor.recordInteraction('Layout mode button clicked');
              dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('layout'));
            }}
          >
            {mobile ? (
              <LayoutLogoIcon sx={{ fontSize: '2rem' }} />
            ) : (
              <>
                <LayoutLogoIcon /> Layout
              </>
            )}
          </ToggleButton>
        </span>
      </Tooltip>

      <Tooltip disableInteractive title="Design Mode (Ctrl + 2)">
        <span>
          <ToggleButton
            value="design"
            sx={props.buttonSx}
            onClick={() => {
              monitor.recordInteraction('Design mode button clicked');
              dispatch(setRenderModeAndEnsureSelectedItemIsAppropriate('design'));
            }}
          >
            {mobile ? (
              <DesignLogoIcon sx={{ fontSize: '2rem' }} />
            ) : (
              <>
                <DesignLogoIcon /> Design
              </>
            )}
          </ToggleButton>
        </span>
      </Tooltip>
    </ToggleButtonGroup>
  );
}
